import axios, { 
  AxiosInstance, 
  AxiosError, 
  InternalAxiosRequestConfig,
  AxiosResponse 
} from 'axios';
import { toast } from 'sonner';
import { API_BASE_URL, API_VERSION, API_TIMEOUT, ERROR_MESSAGES } from './constants';
import { 
  getAuthToken, 
  getRefreshToken, 
  setAuthToken, 
  clearAuthData 
} from './auth';
import { useAuthStore } from '@/store/authStore';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/${API_VERSION}`,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Track if we're currently refreshing token
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle errors and token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // If already refreshing, queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getRefreshToken();
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Attempt to refresh token
        const response = await axios.post(`${API_BASE_URL}/api/${API_VERSION}/auth/refresh`, {
          refreshToken,
        });

        const { token, refreshToken: newRefreshToken } = response.data;
        
        // Store new tokens
        setAuthToken(token);
        if (newRefreshToken) {
          setAuthToken(newRefreshToken);
        }

        // Update auth store
        useAuthStore.getState().setUser(response.data.user);

        // Process queued requests
        processQueue(null, token);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        
        // Clear auth and redirect to login
        clearAuthData();
        useAuthStore.getState().logout();
        
        toast.error(ERROR_MESSAGES.UNAUTHORIZED);
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data as { 
        message?: string; 
        error?: string | { code?: string; message?: string };
      };
      
      // Extract error message from various backend formats
      let message: string = ERROR_MESSAGES.GENERIC;
      if (data?.message) {
        message = data.message;
      } else if (typeof data?.error === 'string') {
        message = data.error;
      } else if (data?.error && typeof data.error === 'object' && data.error.message) {
        message = data.error.message;
      }

      switch (status) {
        case 403:
          toast.error(ERROR_MESSAGES.FORBIDDEN);
          break;
        case 404:
          toast.error(ERROR_MESSAGES.NOT_FOUND);
          break;
        case 422:
          toast.error(ERROR_MESSAGES.VALIDATION);
          break;
        case 500:
        case 502:
        case 503:
          toast.error(ERROR_MESSAGES.GENERIC);
          break;
        default:
          if (status !== 401) {
            toast.error(message);
          }
      }
    } else if (error.request) {
      // Network error
      toast.error(ERROR_MESSAGES.NETWORK);
    }

    return Promise.reject(error);
  }
);

/**
 * Recursively flatten nested relation objects into `<relation>Name` fields.
 * Backend responses include relations like `contact: { id, name }` — pages
 * display flat `contactName`. This maps `contact → contactName`,
 * `product → productName`, `warehouse → warehouseName`, etc. automatically.
 */
function flattenRelations(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(flattenRelations);
  if (typeof obj === 'object') {
    const out: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      out[key] = flattenRelations(value);
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        'name' in (value as any) &&
        typeof (value as any).name === 'string'
      ) {
        out[`${key}Name`] = (value as any).name;
      }
    }
    return out;
  }
  return obj;
}

/**
 * Unwrap the API response envelope for single-resource responses.
 *
 * The backend wraps everything as { success: true, data: T, meta: {...} }.
 * List endpoints return { success, data: T[], meta: { pagination } } and must
 * keep the envelope (pages read .data and .meta.pagination). Single-resource
 * endpoints return { success, data: {...} } — unwrap those to the entity.
 */
function unwrapEnvelope(body: any): any {
  if (
    body &&
    typeof body === 'object' &&
    body.success === true &&
    'data' in body &&
    body.meta?.pagination === undefined
  ) {
    return body.data;
  }
  return body;
}

// API helper methods
export const apiClient = {
  get: <T>(url: string, config = {}) => api.get<T>(url, config).then((res) => flattenRelations(unwrapEnvelope(res.data)) as T),
  
  post: <T>(url: string, data = {}, config = {}) => 
    api.post<T>(url, data, config).then((res) => flattenRelations(unwrapEnvelope(res.data)) as T),
  
  put: <T>(url: string, data = {}, config = {}) => 
    api.put<T>(url, data, config).then((res) => flattenRelations(unwrapEnvelope(res.data)) as T),
  
  patch: <T>(url: string, data = {}, config = {}) => 
    api.patch<T>(url, data, config).then((res) => flattenRelations(unwrapEnvelope(res.data)) as T),
  
  delete: <T>(url: string, config = {}) => api.delete<T>(url, config).then((res) => flattenRelations(unwrapEnvelope(res.data)) as T),
};

/**
 * Download a PDF (or any binary file) from an authenticated endpoint.
 * Fetches with the auth token and triggers a browser download.
 */
export async function downloadFile(path: string, filename: string, mimeType = 'application/pdf'): Promise<void> {
  const response = await api.get(path, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default api;
